/**
 * KnowledgeAskBox — vibe-designer 1.171.0 Phase 4.
 *
 * Top-of-Overview LLM Q&A surface. Two-button form:
 *   [Ask]   → POST /api/knowledge/ask → renders answer + citations + sources footer
 *   [↗ Pi]  → dispatches `vibe:send-to-pi-chat` CustomEvent on window
 *             (matches the 1.162.7 voice → Pi reverse channel pattern;
 *              ChatPanelPi.tsx:597 already listens for this event).
 *
 * 1.196.0 Phase 8.3 — model selector + thinking toggle. UI parity with Pi
 * chat (per user verbatim: "like we have for the mind chant and pi chat").
 * Model list fetched via tenantRouterProvider.fetchTenantRouterModels (same
 * call ChatPanelPi:153-156 makes). Per-tenant localStorage persistence
 * mirrors chatPrefsStore pattern (null-tenant skip-write guard).
 *
 * Honest fallback branches (mirrors KnowledgeServiceDetail Phase 3):
 *   - loading        → spinner
 *   - service-error  → amber banner (response.error set, e.g. LLM down / timeout)
 *   - network-error  → red banner (Promise rejection from fetch)
 *   - answer + cits  → markdown answer + cite list + sources footer
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Loader2, MessageSquare } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { useTenantStore } from '@/stores/tenantStore';
import {
  fetchTenantRouterModels,
} from '@/services/tenantRouterProvider';
import { PillarChips } from './PillarChips';

// 1.196.0 Phase 8.3 — proxy base + token storage key mirror ChatPanelPi
// (see ChatPanelPi.tsx:75 + :156). Single source of truth for chat-proxy URL.
const CHAT_PROXY_BASE_URL = '/api/chat-proxy/v1';
const AUTH_TOKEN_KEY = 'vibe_designer_token';

interface AskModelOption {
  id: string;
  name: string;
}

export function KnowledgeAskBox() {
  const askResponse = useKnowledgeStore((s) => s.askResponse);
  const isAsking = useKnowledgeStore((s) => s.isAsking);
  const askError = useKnowledgeStore((s) => s.askError);
  const runAsk = useKnowledgeStore((s) => s.runAsk);
  // 1.196.0 Phase 8.3 — tenantId required for per-tenant localStorage keys
  // (mirror chatPrefsStore pattern; null-guard at write time).
  const tenantId = useTenantStore((s) => s.tenantId);

  // Phase 9.0 Surface B — vibe:focus-panel listener. When the voice agent
  // emits spa_action(focus_panel, knowledge-ask), this component scrolls
  // itself into view + focuses the input. Single mount-time effect; no
  // teardown needed because the listener is per-component-instance.
  const sectionRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [draft, setDraft] = useState('');
  // 1.184.0 Phase 7.1 — pillar filter on ask. MIND enforces; others no-op.
  const [pillar, setPillar] = useState<string | null>(null);

  // 1.196.0 Phase 8.3 — model selector + thinking toggle state.
  // R1-C4 lock: unified state representation. selectedModel uses '' for
  // "use platform default"; concrete model id otherwise. Falsy check at
  // payload-spread time omits the field naturally.
  const [models, setModels] = useState<AskModelOption[]>([]);
  // R2-C2 fix: track fetch-attempted state separately so we can render a
  // small "(model list unavailable)" hint when fetch returns [] (could be
  // either "no models configured" OR "fetch failed"). tenantRouterProvider
  // returns [] on ANY error; we lose that signal without this state.
  const [modelsFetched, setModelsFetched] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [thinking, setThinking] = useState<boolean>(false);

  // Hydrate persistence from localStorage when tenantId becomes known.
  // R1-C2 lock: null-tenant skip read+write (mirror chatPrefsStore:134-136).
  useEffect(() => {
    if (!tenantId) return;
    const storedModel = localStorage.getItem(`vibe_knowledge_ask_model_${tenantId}`);
    if (storedModel !== null) setSelectedModel(storedModel);
    const storedThinking = localStorage.getItem(`vibe_knowledge_ask_thinking_${tenantId}`);
    if (storedThinking !== null) setThinking(storedThinking === 'true');
  }, [tenantId]);

  // Fetch model list on mount. R1-C1 lock: exact ChatPanelPi arg shape —
  // CHAT_PROXY_BASE_URL + Bearer token from localStorage.
  useEffect(() => {
    let cancelled = false;
    const authToken = localStorage.getItem(AUTH_TOKEN_KEY) ?? '';
    fetchTenantRouterModels(CHAT_PROXY_BASE_URL, authToken).then((entries) => {
      if (cancelled) return;
      setModels(entries.map((e) => ({ id: e.id, name: e.name ?? e.id })));
      setModelsFetched(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist selectedModel on change. R1-C2 null-guard.
  useEffect(() => {
    if (!tenantId) return;
    if (selectedModel) {
      localStorage.setItem(`vibe_knowledge_ask_model_${tenantId}`, selectedModel);
    } else {
      localStorage.removeItem(`vibe_knowledge_ask_model_${tenantId}`);
    }
  }, [selectedModel, tenantId]);

  // Persist thinking on change. R1-C2 null-guard.
  useEffect(() => {
    if (!tenantId) return;
    localStorage.setItem(`vibe_knowledge_ask_thinking_${tenantId}`, thinking ? 'true' : 'false');
  }, [thinking, tenantId]);

  // Phase 9.0 Surface B — listen for voice-driven focus_panel events.
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ panel?: string }>;
      if (ev.detail?.panel !== 'knowledge-ask') return;
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      inputRef.current?.focus();
    };
    window.addEventListener('vibe:focus-panel', handler);
    return () => window.removeEventListener('vibe:focus-panel', handler);
  }, []);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = draft.trim();
    if (!q) return;
    // R1-C3 lock: opts spread per-field; askKnowledge body construction
    // conditionally includes each. Falsy values (empty model, false thinking)
    // are omitted from the payload — backend falls back to its defaults.
    const opts: { pillar_filter?: string; model?: string; thinking?: boolean } = {};
    if (pillar) opts.pillar_filter = pillar;
    // 2026-06-29 — explicit dropdown pick wins; "Platform default"
    // (selectedModel === '') sends NO model so the backend resolves it to
    // settings.KNOWLEDGE_ASK_MODEL (the node1 vLLM coder). Do NOT resolve via
    // fetchDefaultModel — its tier-picker can land on a slow Ollama model,
    // which caused the 60s ask timeouts (node1 coder synthesizes in ~6s).
    if (selectedModel) {
      opts.model = selectedModel;
    }
    if (thinking) opts.thinking = true;
    void runAsk(q, Object.keys(opts).length > 0 ? opts : undefined);
  };

  const onAskInPi = () => {
    const q = draft.trim();
    if (!q) return;
    window.dispatchEvent(
      new CustomEvent('vibe:send-to-pi-chat', { detail: { text: q } }),
    );
  };

  return (
    <section
      ref={sectionRef}
      data-testid="knowledge-ask-section"
    >
      <header className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary mb-2">
        Ask the corpus
      </header>
      <PillarChips value={pillar} onChange={setPillar} className="mb-3" />
      {/* 1.196.0 Phase 8.3 — model selector + thinking toggle row */}
      <div className="flex items-center gap-2 mb-3 text-xs">
        <label className="text-vibe-text-secondary" htmlFor="knowledge-ask-model-select">
          Model:
        </label>
        <select
          id="knowledge-ask-model-select"
          data-testid="knowledge-ask-model-select"
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
          disabled={isAsking}
          className="px-2 py-1 rounded bg-vibe-bg border border-vibe-border text-vibe-text disabled:opacity-50"
        >
          <option value="">Platform default</option>
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
        {/* R2-C2 fix: honest signal when model fetch returned empty list
            (could be no-models-configured OR fetch-failed; tenantRouterProvider
            swallows both into []). Submit still works using platform default. */}
        {modelsFetched && models.length === 0 && (
          <span
            data-testid="knowledge-ask-model-list-empty"
            className="text-vibe-text-secondary italic"
            title="Could not load model list from tenant LLM router; default still works"
          >
            (model list unavailable)
          </span>
        )}
        <button
          data-testid="knowledge-ask-thinking-toggle"
          type="button"
          onClick={() => setThinking((v) => !v)}
          disabled={isAsking}
          aria-pressed={thinking}
          title={thinking ? 'Thinking mode ON — model emits reasoning chain' : 'Thinking mode off'}
          className={`px-2 py-1 rounded border border-vibe-border disabled:opacity-50 ${
            thinking ? 'bg-vibe-accent/20 text-vibe-accent' : 'bg-vibe-bg text-vibe-text-secondary'
          }`}
        >
          Thinking: {thinking ? 'on' : 'off'}
        </button>
      </div>
      <form onSubmit={onSubmit} className="flex gap-2 mb-3">
        <input
          ref={inputRef}
          data-testid="knowledge-ask-input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask a question…"
          className="flex-1 px-3 py-1.5 text-sm rounded bg-vibe-bg border border-vibe-border text-vibe-text placeholder:text-vibe-text-secondary"
          disabled={isAsking}
        />
        <button
          data-testid="knowledge-ask-submit"
          type="submit"
          disabled={isAsking || draft.trim().length === 0}
          className="px-3 py-1.5 text-sm rounded bg-vibe-accent text-vibe-bg disabled:opacity-50"
        >
          {isAsking ? 'Asking…' : 'Ask'}
        </button>
        {/* R2 #6 — Pi deep-link intentionally NOT gated on `isAsking`. The
            "Ask the corpus" and "Ask in Pi chat" actions are independent;
            a user mid-LLM-flight can still re-route the same draft to Pi
            without waiting for the ask to complete. */}
        <button
          data-testid="knowledge-ask-pi-deeplink"
          type="button"
          onClick={onAskInPi}
          disabled={draft.trim().length === 0}
          title="Send this question to the Pi chat agent"
          className="px-3 py-1.5 text-sm rounded border border-vibe-border text-vibe-text disabled:opacity-50 flex items-center gap-1"
        >
          <MessageSquare className="w-4 h-4" />
          <span>Ask in Pi chat ↗</span>
        </button>
      </form>

      {isAsking && (
        <div
          data-testid="knowledge-ask-loading"
          className="flex items-center gap-2 text-sm text-vibe-text-secondary py-2"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Asking the corpus…</span>
        </div>
      )}

      {!isAsking && askError && (
        <div
          data-testid="knowledge-ask-network-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-3 text-sm text-vibe-error"
        >
          Network error: {askError.message}
        </div>
      )}

      {!isAsking && askResponse && askResponse.error && askResponse.answer === null && (
        <div
          data-testid="knowledge-ask-error"
          className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-3 text-sm text-vibe-warning"
        >
          {askResponse.error}
        </div>
      )}

      {!isAsking && askResponse && askResponse.answer !== null && (
        <>
          <div
            data-testid="knowledge-ask-answer"
            className="rounded border border-vibe-border bg-vibe-bg p-3 mb-2 text-sm text-vibe-text whitespace-pre-wrap"
          >
            {askResponse.answer}
          </div>

          {askResponse.citations.length > 0 && (
            <div data-testid="knowledge-ask-citations" className="mb-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary mb-1">
                Citations
              </div>
              <ul className="space-y-1">
                {askResponse.citations.map((c, i) => (
                  <li
                    key={`${c.service}-${i}`}
                    data-testid={`knowledge-ask-citation-${c.service}-${i}`}
                    className="text-xs flex items-baseline gap-2"
                  >
                    <span className="px-1.5 py-0.5 rounded bg-vibe-bg-tertiary uppercase font-mono text-vibe-text-secondary">
                      {c.service}
                    </span>
                    <span className="font-medium text-vibe-text">{c.title}</span>
                    <span className="text-vibe-text-secondary truncate flex-1">{c.snippet.slice(0, 120)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {askResponse.sources_consulted.length > 0 && (
            <div
              data-testid="knowledge-ask-sources-footer"
              className="text-xs text-vibe-text-secondary border-t border-vibe-border pt-2"
            >
              <div className="font-semibold mb-1">Sources consulted</div>
              <ul className="space-y-0.5">
                {askResponse.sources_consulted.map((s) => (
                  <li key={s.service} className="flex items-center gap-2">
                    <span className="uppercase font-mono w-24">{s.service}</span>
                    <span className={s.available ? 'text-vibe-success' : 'text-vibe-text-secondary'}>
                      {s.available ? `${s.hits_used} hit${s.hits_used === 1 ? '' : 's'}` : 'unavailable'}
                    </span>
                    {s.error && <span className="text-vibe-warning text-xs truncate">{s.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
